import type { PublicSettings } from '@brandonstrong/types';
import { clockTime, stayDate } from '../lib/format';
import type { StayParams } from '../lib/stayParams';

interface StayPickerProps {
  stay: StayParams;
  settings: PublicSettings | undefined;
  onChange: (patch: Partial<StayParams>) => void;
  compact?: boolean;
}

const DAY_MS = 86_400_000;
const nightsBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);

/**
 * Check-in / check-out dates constrained to the bookable window. When the
 * window is a single night (the celebration: October 3 only) there is nothing
 * to choose, so the night is stated instead of asked for. Guest counts are no
 * longer asked for either (the family dropped occupancy from the site on
 * 2026-09-08); the stay keeps its default counts for the reservation record.
 */
export function StayPicker({ stay, settings, onChange, compact }: StayPickerProps) {
  const min = settings?.bookableCheckInMin ?? undefined;
  const max = settings?.bookableCheckOutMax ?? undefined;
  const singleNight = Boolean(min && max && nightsBetween(min, max) === 1);

  if (singleNight && min && max) {
    return (
      <div className={compact ? '' : 'md:max-w-md'}>
        <p className="text-meta text-sm">Your night</p>
        <p className="mt-1 text-lg text-ink-50">
          Check in {stayDate(min)}
          {settings ? ` from ${clockTime(settings.checkInTime)}` : ''}, check out {stayDate(max)}
          {settings ? ` by ${clockTime(settings.checkOutTime)}` : ''}.
        </p>
        <p className="mt-1 text-sm text-ink-400">One night; that is the whole celebration.</p>
      </div>
    );
  }

  return (
    <div className={`grid gap-3 ${compact ? 'grid-cols-2' : 'grid-cols-2 md:max-w-md'}`}>
      <label className="flex flex-col gap-1">
        <span className="text-meta text-sm">Check-in</span>
        <input
          type="date"
          className="input-night"
          value={stay.checkIn ?? ''}
          min={min}
          max={max}
          onChange={(e) => onChange({ checkIn: e.target.value || null })}
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-meta text-sm">Check-out</span>
        <input
          type="date"
          className="input-night"
          value={stay.checkOut ?? ''}
          min={stay.checkIn ?? min}
          max={max}
          onChange={(e) => onChange({ checkOut: e.target.value || null })}
        />
      </label>
    </div>
  );
}
